import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { LiveState } from '../../api/schema'
import { ballotApi, followLiveStream } from '../../hooks/useBallots'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'

const VOTES = ['for', 'against', 'abstain'] as const

/**
 * The participant's screen while the meeting is held (spec 2026-10-05-09):
 * check in by QR code (the link opened on their own phone, identified), vote
 * on the scrutin open now for themselves and their mandators, and, in a hybrid
 * meeting, the video, a raised hand and written questions. Shows nothing
 * before the meeting.
 */
export function LiveSection({ orgCode, session, onError }: { orgCode: string; session: string; onError: (e: unknown) => boolean }) {
    const { t } = useTranslation('ballots')
    const [live, setLive] = useState<LiveState | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const [question, setQuestion] = useState('')
    const [sent, setSent] = useState(false)

    const refresh = useCallback(async () => {
        try {
            setLive(await ballotApi.live(orgCode, session))
        } catch (e) {
            if (!onError(e)) setLive(null)
        }
    }, [orgCode, session, onError])

    useEffect(() => {
        void refresh()
        const stop = followLiveStream(orgCode, session, () => void refresh())
        const poll = window.setInterval(() => void refresh(), 10000)
        return () => {
            stop()
            window.clearInterval(poll)
        }
    }, [orgCode, session, refresh])

    const run = async (fn: () => Promise<LiveState>) => {
        setBusy(true)
        setError(null)
        try {
            setLive(await fn())
        } catch (e) {
            if (!onError(e)) setError(e instanceof Error ? e.message : String(e))
        } finally {
            setBusy(false)
        }
    }

    if (!live || live.status !== 'in_progress') return null
    const round = live.round
    return (
        <section className="space-y-4 rounded-md border p-4" data-testid="ballot-live">
            <h2 className="text-lg font-semibold">{t('live.title')}</h2>
            {live.sitting_status === 'suspended' && (
                <Alert variant="warning" data-testid="ballot-live-suspended">
                    <AlertTitle>{t('live.suspended')}</AlertTitle>
                    <AlertDescription>{t('live.suspended_body')}</AlertDescription>
                </Alert>
            )}
            {!live.present ? (
                <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">{t('live.not_present')}</p>
                    <Button disabled={busy} data-testid="ballot-live-checkin" onClick={() => run(() => ballotApi.checkIn(orgCode, session))}>
                        {t('live.check_in')}
                    </Button>
                </div>
            ) : (
                <p className="text-sm" data-testid="ballot-live-present">{t('live.present')}</p>
            )}
            {live.present && round ? (
                <div className="space-y-3" data-testid="ballot-live-round">
                    <p className="font-medium">{round.number}. {round.title}</p>
                    <p className="whitespace-pre-line text-sm text-muted-foreground">{round.text}</p>
                    <div className="flex flex-wrap gap-2">
                        {VOTES.map((v) => (
                            <Button
                                key={v}
                                variant={live.my_vote === v ? 'default' : 'outline'}
                                disabled={busy || live.sitting_status !== 'running'}
                                data-testid={`ballot-live-vote-${v}`}
                                onClick={() => run(async () => (await ballotApi.liveVote(orgCode, session, { resolution_uid: round.resolution_uid, vote: v })).state)}
                            >
                                {t(`vote.${v}`)}
                            </Button>
                        ))}
                    </div>
                    {live.my_vote && <p className="text-xs text-muted-foreground" data-testid="ballot-live-my-vote">{t('live.recorded', { vote: t(`vote.${live.my_vote}`) })}</p>}
                    {live.mandators.map((md) => (
                        <div key={md.stakeholder_uid} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`ballot-live-mandator-${md.stakeholder_uid}`}>
                            <span>{t('live.for_mandator', { name: md.name, voices: md.voices })}</span>
                            {VOTES.map((v) => (
                                <Button key={v} size="sm" variant={md.vote === v ? 'default' : 'outline'} disabled={busy || live.sitting_status !== 'running'}
                                    onClick={() => run(async () => (await ballotApi.liveVote(orgCode, session, { resolution_uid: round.resolution_uid, vote: v, for_stakeholder_uid: md.stakeholder_uid })).state)}>
                                    {t(`vote.${v}`)}
                                </Button>
                            ))}
                        </div>
                    ))}
                </div>
            ) : live.present ? (
                <p className="text-sm text-muted-foreground" data-testid="ballot-live-waiting">{t('live.waiting')}</p>
            ) : null}
            {live.hybrid && live.video_url && (
                <div className="space-y-2" data-testid="ballot-live-hybrid">
                    <iframe title={t('live.video')} src={live.video_url} allow="camera; microphone; fullscreen" className="h-72 w-full rounded-md border" />
                    {live.audio_fallback && <p className="text-xs text-muted-foreground">{t('live.audio', { dial: live.audio_fallback })}</p>}
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" disabled={busy} data-testid="ballot-live-hand"
                            onClick={() => run(() => ballotApi.question(orgCode, session, { kind: 'hand' }))}>{t('live.raise_hand')}</Button>
                    </div>
                    <Textarea rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t('live.question_placeholder')} data-testid="ballot-live-question" />
                    <Button size="sm" disabled={busy || !question.trim()} data-testid="ballot-live-question-send" onClick={() => run(async () => {
                        const s = await ballotApi.question(orgCode, session, { kind: 'question', body: question })
                        setQuestion('')
                        setSent(true)
                        return s
                    })}>{t('live.send_question')}</Button>
                    {sent && <p className="text-xs text-muted-foreground">{t('live.question_sent')}</p>}
                </div>
            )}
            {error && (
                <Alert variant="destructive" data-testid="ballot-live-error">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}
        </section>
    )
}
